import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import {io, Socket} from 'socket.io-client';
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Divider,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Alert,
  Tooltip,
  Fab,
  Badge,
  CircularProgress
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import {environment} from '@shared/config/environment';
import {createChatBridge} from './chat-bridge';
import AddIcon from '@mui/icons-material/Add';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DescriptionIcon from '@mui/icons-material/Description';
import LanguageIcon from '@mui/icons-material/Language';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {sanitizeHtml} from 'src/utils/htmlSanitizer';

/** ---------- Constants & Helpers ---------- */

const FALLBACK_CATALOG = {
  departments: [
    {
      departmentId: 'dept:safety-protocools',
      department: 'Safety Protocools',
      subdepartments: [
        {subDepartmentId: 'subdept:safety-children', subDepartment: 'Children'},
        {subDepartmentId: 'subdept:safety-weather', subDepartment: 'Weather'},
        {subDepartmentId: 'subdept:safety-fire', subDepartment: 'Fire'},
        {subDepartmentId: 'subdept:safety-weapons', subDepartment: 'Weapons'},
        {
          subDepartmentId: 'subdept:safety-code-gray',
          subDepartment: 'Code Gray'
        }
      ]
    },
    {
      departmentId: 'dept:clinical',
      department: 'Clinical',
      subdepartments: [
        {
          subDepartmentId: 'subdept:clinical-admission',
          subDepartment: 'Admission'
        },
        {
          subDepartmentId: 'subdept:clinical-discharge',
          subDepartment: 'Discharge'
        }
      ]
    },
    {
      departmentId: 'dept:fin',
      department: 'FIN',
      subdepartments: [
        {subDepartmentId: 'subdept:fin-auth', subDepartment: 'Auth'},
        {subDepartmentId: 'subdept:fin-comp', subDepartment: 'Comp'}
      ]
    }
  ]
};

const USER = {
  id: 'f4576fae-7dec-11d0-a765-0067c91e6bf6',
  roles: ['user'],
  permissions: ['read']
};

/* const rawBase = environment?.EnterpriceSearchServer ?? "https://pmsai-dev.bytepad.com";
const SERVER_BASE = String(rawBase).replace(/\/$/, '');
//console.log('SERVER URL', rawBase);

const toServerUrl = (u?: string) => {
  if (!u) return u;
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  if (u.startsWith('/')) return `${SERVER_BASE}${u}`;
  return u;
}; */

const isAzureBlobUrl = (u?: string) => {
  try {
    if (!u) return false;
    const url = new URL(u);
    return url.hostname.includes('.blob.core.windows.net');
  } catch {
    return false;
  }
};

const extractWebSources = (text?: string) => {
  const out: {url: string; title?: string}[] = [];
  const seen = new Set<string>();
  if (!text || typeof text !== 'string') return out;

  try {
    const supMd =
      /<sup>\s*\[\s*(\d+)\s*\]\((https?:\/\/[^\s)]+)(?:\s+"([^"]*)")?\)\s*<\/sup>/gi;
    let m: RegExpExecArray | null;
    while ((m = supMd.exec(text)) !== null) {
      const url = m[2];
      const title = (m[3] || '').trim();
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push({url, title});
      }
    }
  } catch {}

  try {
    const supA =
      /<sup>[^<]*\[\s*(\d+)\s*\][^<]*<a[^>]+href="(https?:\/\/[^"]+)"[^>]*>(.*?)<\/a>[^<]*<\/sup>/gi;
    let m: RegExpExecArray | null;
    while ((m = supA.exec(text)) !== null) {
      const url = m[2];
      const title = (m[3] || '').replace(/<[^>]+>/g, '').trim();
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push({url, title});
      }
    }
  } catch {}

  try {
    const a = /<a[^>]+href="(https?:\/\/[^"]+)"[^>]*>(.*?)<\/a>/gi;
    let m: RegExpExecArray | null;
    while ((m = a.exec(text)) !== null) {
      const url = m[1];
      const title = (m[2] || '').replace(/<[^>]+>/g, '').trim();
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push({url, title});
      }
    }
  } catch {}

  try {
    const bare = /(https?:\/\/[^\s)<>"]+)/g;
    let m: RegExpExecArray | null;
    while ((m = bare.exec(text)) !== null) {
      const url = m[1];
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push({url, title: ''});
      }
    }
  } catch {}

  return out;
};

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderMarkdown = (text?: string) => {
  if (!text) return '';
  const escaped = escapeHtml(String(text));
  let html = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
  html = html.replace(/^[\-\*]\s+(.*)$/gm, '• $1');
  html = html.replace(/(https?:\/\/[^\s]+)|(www\.[^\s]+)/g, m => {
    const url = m.startsWith('http') ? m : `http://${m}`;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${m}</a>`;
  });
  html = html.replace(/\n/g, '<br/>');
  html = html.replace(/â€¢/g, '&bull;').replace(/\uFFFD/g, '-');
  return html;
};

const truncateText = (text: string, maxLength: number = 30) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/** ---------- Types ---------- */

type MsgRole = 'user' | 'assistant' | 'error';
type Msg = {
  role: MsgRole;
  text: string;
  streaming?: boolean;
  final?: boolean;
  vector?: any[];
  thumbnails?: Array<any>;
  webSources?: {url: string; title?: string}[];
  sources?: string[];
  provenance?: string | null;
};

type AssistantPayload = {
  text?: string;
  vector?: {matches?: any[]};
  thumbnails?: any[];
  webSources?: {url: string; title?: string}[];
  sources?: string[];
};

interface Session {
  sessionId: string;
  userId: string;
  title: string;
  roles: string[];
  permissions: string[];
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string;
  messageCount: number;
  status: string;
}

/** ---------- Animated Thinking Indicator Component ---------- */
const ThinkingIndicator: React.FC = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 1
      }}>
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'success.main',
            animation: 'pulse 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes pulse': {
              '0%, 80%, 100%': {
                opacity: 0.4,
                transform: 'scale(1)'
              },
              '40%': {
                opacity: 1,
                transform: 'scale(1.2)'
              }
            }
          }}
        />
      ))}
      <Typography
        variant="caption"
        sx={{ml: 1, color: 'text.secondary', fontStyle: 'italic'}}>
        Thinking...
      </Typography>
    </Box>
  );
};

/** ---------- Component ---------- */

const GlobalChatWidget: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState('');
  const [enlargedImage, setEnlargedImage] = useState<any>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [status, setStatus] = useState('');
  const [hasError, setHasError] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isAssistantThinking, setIsAssistantThinking] = useState(false);
  const [userId] = useState('currentUser123');
  const [staticSessionId] = useState('52e68a21-ce8b-45fc-aced-f7b4a0afcaf1');
  const [hoveredSessionId, setHoveredSessionId] = useState<string | null>(null);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [responseMode, setResponseMode] = useState<string>(() => {
    try {
      return localStorage.getItem('responseMode') || 'summary';
    } catch {
      return 'summary';
    }
  });
  const [source, setSource] = useState<string>(() => {
    try {
      return localStorage.getItem('source') || 'documents_and_web';
    } catch {
      return 'documents_and_web';
    }
  });
  const [vectorMatches, setVectorMatches] = useState<any[]>([]);
  const [catalog] = useState(FALLBACK_CATALOG);
  const [selectedTree, setSelectedTree] = useState<
    Record<string, {name: string; subs: Set<string>}>
  >({});

  const containerRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const initialLoadRef = useRef(false);

  const effectiveCatalog = useMemo(() => {
    try {
      if (
        catalog &&
        Array.isArray(catalog.departments) &&
        catalog.departments.length > 0
      ) {
        return catalog;
      }
    } catch {}
    return FALLBACK_CATALOG;
  }, [catalog]);

  const SERVER_BASE = useMemo(() => {
    console.log("environment?.EnterpriceSearchServer", environment);
    const rawBase = environment?.EnterpriceSearchServer ?? 'https://bytepad-enterprise-sear-api.azurewebsites.net';
    return String(rawBase || '').replace(/\/$/, '');
  }, [environment]);
  console.log("SERVER_BASE", SERVER_BASE,environment?.EnterpriceSearchServer);
  const toServerUrl = (u?: string) => {
    if (!u) return u;
    if (
      u.startsWith('http://') ||
      u.startsWith('https://') ||
      u.startsWith('data:')
    )
      return u;
    if (u.startsWith('/')) return `${SERVER_BASE}${u}`;
    return u;
  };

  const setupSocketListeners = useCallback((s: Socket) => {
    s.on('connect', () => {
      setIsConnected(true);
      setStatus('Connected to server');
      setHasError(false);
    });

    s.on('disconnect', () => {
      setIsConnected(false);
      setStatus('Disconnected from server');
      setHasError(true);
    });

    s.on('chat_joined', (data: any) => {
      setStatus(data?.message || 'Joined chat');
      setHasError(false);
    });

    s.on('assistant_message', (data: AssistantPayload) => {
      setIsAssistantThinking(false);

      const vectorMatches =
        data.vector && Array.isArray(data.vector.matches)
          ? data.vector.matches
          : [];
      const thumbs = Array.isArray(data.thumbnails)
        ? data.thumbnails.map((t: any) => ({
            ...t,
            thumbUrl: toServerUrl(t.thumbUrl),
            linkUrl: toServerUrl(t.linkUrl)
          }))
        : [];

      let provenance: string | null = null;
      let fullText = data.text || '';
      try {
        const lines = (fullText || '').split(/\r?\n/);
        if (lines.length && /^\s*generated from:/i.test(lines[0])) {
          provenance = lines[0].split(':', 2)[1]?.trim() || null;
          const rest = lines.slice(1);
          if (rest.length && rest[0].trim() === '') rest.shift();
          fullText = rest.join('\n');
        }
      } catch {}

      const serverWebSources = Array.isArray(data.webSources)
        ? data.webSources
        : [];
      const rawWebSources = extractWebSources(fullText);
      const combined = (
        serverWebSources.length ? serverWebSources : rawWebSources
      ).filter(s => s && s.url && !isAzureBlobUrl(s.url));
      const sources = Array.isArray(data.sources) ? data.sources : [];
      const webSources =
        Array.isArray(sources) && sources.includes('web') ? combined : [];

      setMessages(prev => {
        const out = [...prev];
        if (out.length && out[out.length - 1].role === 'assistant') {
          out[out.length - 1] = {
            ...out[out.length - 1],
            text: fullText,
            streaming: false,
            final: true,
            vector: vectorMatches,
            thumbnails: thumbs,
            webSources,
            sources,
            provenance
          };
          return out;
        }
        return [
          ...out,
          {
            role: 'assistant',
            text: fullText,
            final: true,
            vector: vectorMatches,
            thumbnails: thumbs,
            webSources,
            sources,
            provenance
          }
        ];
      });
    });

    s.on('assistant_delta', (data: {delta?: string; text?: string}) => {
      setIsAssistantThinking(false);

      const delta = (data && (data.delta || data.text)) || '';
      if (!delta) return;

      setMessages(prev => {
        const out = [...prev];
        const last = out[out.length - 1];
        if (!last || last.role !== 'assistant' || last.final) {
          out.push({role: 'assistant', text: delta, streaming: true});
        } else {
          last.text = (last.text || '') + delta;
          last.streaming = true;

          try {
            if (!last.provenance) {
              const lines = String(last.text || '').split(/\r?\n/);
              if (lines.length && /^\s*generated from:/i.test(lines[0])) {
                last.provenance = lines[0].split(':', 2)[1]?.trim() || null;
                let rest = lines.slice(1);
                if (rest.length && rest[0].trim() === '') rest = rest.slice(1);
                last.text = rest.join('\n');
              }
            }
          } catch {}

          out[out.length - 1] = {...last};
        }
        return out;
      });
    });

    s.on('search_progress', (data: any) => {
      setIsAssistantThinking(true);
      setStatus(`Processing: ${data?.state ?? 'working'}`);
    });

    s.on('search_complete', () => {
      setIsAssistantThinking(false);
      setStatus('');
    });

    s.on('vector_results', (payload: {matches?: any[]}) => {
      try {
        if (payload && Array.isArray(payload.matches))
          setVectorMatches(payload.matches);
      } catch (e) {
        console.warn('Failed to handle vector_results', e);
      }
    });

    s.on('search_error', (data: {message: string}) => {
      setIsAssistantThinking(false);
      setMessages(prev => [...prev, {role: 'error', text: data.message}]);
      setStatus(`Error: ${data.message}`);
      setHasError(true);
    });

    s.on('connect_error', (error: any) => {
      setStatus(`Connection error: ${error?.message || 'unknown'}`);
      setHasError(true);
      setIsAssistantThinking(false);
    });
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!sessionId || loadingOlderMessages || !hasMoreMessages) return;

    try {
      setLoadingOlderMessages(true);
      const nextPage = currentPage + 1;

      const container = messagesContainerRef.current;
      const previousScrollHeight = container?.scrollHeight || 0;

      const res = await axios.get(
        `${SERVER_BASE}/api/chat/sessions/${sessionId}/messages`,
        {
          params: {userId: USER.id, page: nextPage, pageSize: 20}
        }
      );

      // Use API response structure
      const responseData = res.data;
      const olderMessages = responseData.messages || [];
      const hasMore = responseData.hasMore ?? false;
      const totalMessages = responseData.totalMessages || 0;
      const totalPages = responseData.totalPages || 1;

      //console.log('Load more response:', { hasMore, totalMessages, totalPages, messagesCount: olderMessages.length });

      // Check if we've loaded all messages
      if (olderMessages.length === 0 || !hasMore || nextPage > totalPages) {
        setHasMoreMessages(false);
      } else {
        // Prepend older messages (already in correct order from server)
        setMessages(prev => {
          // Prevent duplicates
          const existingIds = new Set(
            prev.map((m: any) => m.messageId || m.timestamp)
          );
          const newMessages = olderMessages.filter(
            (m: any) => !existingIds.has(m.messageId || m.timestamp)
          );
          return [...newMessages, ...prev];
        });
        setCurrentPage(nextPage);
        setHasMoreMessages(hasMore);

        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - previousScrollHeight;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Failed to load older messages:', error);
      setHasMoreMessages(false);
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [sessionId, currentPage, loadingOlderMessages, hasMoreMessages]);
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const scrollTop = container.scrollTop;

      if (scrollTop < 100 && !loadingOlderMessages && hasMoreMessages) {
        loadMoreMessages();
      }
    },
    [loadingOlderMessages, hasMoreMessages, loadMoreMessages]
  );

  const createSession = useCallback(async () => {
    try {
      setStatus('Creating session...');
      setHasError(false);
      const response = await axios.post(`${SERVER_BASE}/api/chat/sessions`, {
        userId: USER.id,
        title: 'New Chat',
        roles: USER.roles,
        permissions: USER.permissions
      });
      const newSessionId = response.data.sessionId;
      setSessionId(newSessionId);
      setActiveSession(response.data);
      setIsFirstMessage(true);
      setCurrentPage(1);
      setHasMoreMessages(true);
      setStatus('Session created, connecting to socket...');

      const s = io(SERVER_BASE, {
        path: '/ws/socket.io',
        transports: ['websocket', 'polling'],
        withCredentials: true,
        autoConnect: true
      });

      setupSocketListeners(s);

      s.on('connect', () => {
        s.emit('start_chat', {
          sessionId: newSessionId,
          userId: USER.id,
          roles: USER.roles,
          permissions: USER.permissions
        });
        setStatus('Joined chat session');
        setHasError(false);
      });

      setSocket(s);
      fetchSessions();
      return newSessionId;
    } catch (error: any) {
      setStatus(`Session creation failed: ${error?.message || 'unknown'}`);
      setHasError(true);
      throw error;
    }
  }, [setupSocketListeners]);

  const startNewSession = useCallback(async () => {
    try {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      setMessages([]);
      setStatus('Starting new session...');
      setHasError(false);
      setSessionId(null);
      setActiveSession(null);
      await createSession();
    } catch {
      setStatus('Failed to start new session');
      setHasError(true);
    }
  }, [socket, createSession]);

  const toggleTreeSelect = useCallback(
    (deptId: string, subId?: string, deptName?: string) => {
      setSelectedTree(prev => {
        const next = {...prev};
        if (!next[deptId])
          next[deptId] = {name: deptName || deptId, subs: new Set<string>()};
        if (subId) {
          if (next[deptId].subs.has(subId)) next[deptId].subs.delete(subId);
          else next[deptId].subs.add(subId);
        } else {
          const catDept = (effectiveCatalog?.departments || []).find(
            d => d.departmentId === deptId
          );
          const allSubs = new Set(
            (catDept?.subdepartments || []).map(s => s.subDepartmentId)
          );
          const hasAll = [...allSubs].every(id => next[deptId].subs.has(id));
          next[deptId].subs = hasAll ? new Set() : allSubs;
        }
        return next;
      });
    },
    [effectiveCatalog]
  );

  const buildNestedDepartmentPayload = useCallback(() => {
    const out: Array<{
      departmentId: string;
      department: string;
      subdepartments: Array<{subDepartmentId: string; subDepartment: string}>;
    }> = [];
    const depts = effectiveCatalog?.departments || [];
    Object.entries(selectedTree || {}).forEach(([deptId, entry]) => {
      const dept = depts.find(d => d.departmentId === deptId);
      if (!dept) return;
      const subEntries: Array<{
        subDepartmentId: string;
        subDepartment: string;
      }> = [];
      const chosen = entry.subs || new Set<string>();
      (dept.subdepartments || []).forEach(s => {
        if (chosen.has(s.subDepartmentId)) {
          subEntries.push({
            subDepartmentId: s.subDepartmentId,
            subDepartment: s.subDepartment
          });
        }
      });
      if (subEntries.length > 0) {
        out.push({
          departmentId: dept.departmentId,
          department: dept.department,
          subdepartments: subEntries
        });
      }
    });
    return out;
  }, [effectiveCatalog, selectedTree]);

  const bridge = useMemo(
    () =>
      createChatBridge({
        getSocket: () => socket,
        getSessionId: () => sessionId,
        autoEmitOnRoute: true,
        consentPrompt: async ({action, args}) => {
          const ok = window.confirm(
            `Allow action "${action}"?\n\n${JSON.stringify(args, null, 2)}`
          );
          return ok ? 'allow' : 'deny';
        }
      }),
    [socket, sessionId]
  );

  const updateSessionTitle = useCallback(
    async (sessionId: string, firstMessage: string) => {
      try {
        const newTitle = truncateText(firstMessage, 30);
        await axios.post(`${SERVER_BASE}/api/chat/sessions/${sessionId}`, {
          userId: USER.id,
          title: newTitle
        });
        setSessions(prev =>
          prev.map(s =>
            s.sessionId === sessionId ? {...s, title: newTitle} : s
          )
        );
        if (activeSession?.sessionId === sessionId) {
          setActiveSession(prev => (prev ? {...prev, title: newTitle} : null));
        }
      } catch (err) {
        console.error('Failed to update session title', err);
      }
    },
    [activeSession]
  );

  const sendMessage = useCallback(() => {
    if (!text.trim()) return;
    if (!sessionId || !socket || !isConnected) {
      setStatus('Please connect to a session first');
      setHasError(true);
      return;
    }

    const depPayload = buildNestedDepartmentPayload();
    const extras = bridge.buildPayloadExtras();

    setMessages(prev => [...prev, {role: 'user', text}]);
    setIsAssistantThinking(true);

    if (isFirstMessage && sessionId) {
      updateSessionTitle(sessionId, text);
      setIsFirstMessage(false);
    }

    socket.emit('user_message', {
      sessionId,
      userId: USER.id,
      text,
      roles: USER.roles,
      permissions: USER.permissions,
      department: depPayload,
      responseMode,
      source,
      ...extras
    });

    setText('');
    setStatus('');
    setHasError(false);
  }, [
    text,
    sessionId,
    socket,
    isConnected,
    buildNestedDepartmentPayload,
    bridge,
    responseMode,
    source,
    isFirstMessage,
    updateSessionTitle
  ]);

  const connectionStatus = useCallback(() => {
    if (!socket) return 'No connection';
    if (!isConnected) return 'Connecting...';
    if (!sessionId) return 'No active session';
    return 'Connected & Ready';
  }, [socket, isConnected, sessionId]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${SERVER_BASE}/api/chat/sessions?userId=${USER.id}`
      );
      setSessions(res.data);
    } catch (err) {
      console.error('Failed to load sessions', err);
    }
  }, []);

  const handleDeleteSession = useCallback(
    async (session: Session, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!window.confirm(`Delete session "${session.title}"?`)) return;
      try {
        await axios.delete(
          `${SERVER_BASE}/api/chat/sessions/${session.sessionId}`,
          {
            data: {userId: USER.id}
          }
        );
        setSessions(prev =>
          prev.filter(s => s.sessionId !== session.sessionId)
        );
        if (activeSession?.sessionId === session.sessionId) {
          setActiveSession(null);
          setMessages([]);
          setSessionId(null);
        }
      } catch (err) {
        console.error('Failed to delete session', err);
      }
    },
    [activeSession]
  );

  const handleEditSession = useCallback(
    async (session: Session, e: React.MouseEvent) => {
      e.stopPropagation();
      const newTitle = prompt('Enter new session name:', session.title);
      if (!newTitle || newTitle === session.title) return;
      try {
        await axios.post(
          `${SERVER_BASE}/api/chat/sessions/rename/${session.sessionId}`,
          {
            userId: USER.id,
            title: newTitle
          }
        );
        setSessions(prev =>
          prev.map(s =>
            s.sessionId === session.sessionId ? {...s, title: newTitle} : s
          )
        );
        if (activeSession?.sessionId === session.sessionId) {
          setActiveSession(prev => (prev ? {...prev, title: newTitle} : null));
        }
      } catch (err) {
        console.error('Failed to edit session', err);
      }
    },
    [activeSession]
  );

  const handleSessionSelect = useCallback(
    async (session: Session) => {
      try {
        if (socket) {
          socket.disconnect();
          setSocket(null);
          setIsConnected(false);
        }

        setActiveSession(session);
        setSessionId(session.sessionId);
        setMessages([]);
        setLoadingMessages(true);
        setIsFirstMessage(false);
        setCurrentPage(1);

        const res = await axios.get(
          `${SERVER_BASE}/api/chat/sessions/${session.sessionId}/messages`,
          {
            params: {userId: USER.id, page: 1, pageSize: 20}
          }
        );

        const responseData = res.data;
        const messagesList = responseData.messages || [];
        const hasMore = responseData.hasMore ?? false;

        // Messages already in correct order from server
        setMessages(messagesList);
        setHasMoreMessages(hasMore);

        const s = io(SERVER_BASE, {
          path: '/ws/socket.io',
          transports: ['websocket', 'polling'],
          withCredentials: true,
          autoConnect: true,
          forceNew: true
        });

        setupSocketListeners(s);

        s.on('connect', () => {
          s.emit('start_chat', {
            sessionId: session.sessionId,
            userId: USER.id,
            roles: USER.roles,
            permissions: USER.permissions
          });
          setStatus('Connected to session');
          setHasError(false);
          setIsConnected(true);
        });

        setSocket(s);
        setShowDrawer(false);
      } catch (err) {
        console.error('Failed to load session', err);
        setStatus('Failed to load session');
        setHasError(true);
      } finally {
        setLoadingMessages(false);
      }
    },
    [socket, setupSocketListeners]
  );

  // Effect 1: Create portal mount point
  useEffect(() => {
    const div = document.createElement('div');
    div.id = '__global_ai_chat_root';
    div.style.zIndex = '2147483647';
    document.body.appendChild(div);
    containerRef.current = div;
    return () => {
      div.remove();
    };
  }, []);

  // Effect 2: Load default session - with ref to prevent double loading
  useEffect(() => {
    if (initialLoadRef.current) return;
    if (SERVER_BASE) {
      const loadDefaultSession = async () => {
        if (!staticSessionId.trim()) return;

        initialLoadRef.current = true;

        try {
          const sessionIdToLoad = staticSessionId.trim();
          setSessionId(sessionIdToLoad);
          setLoadingMessages(true);
          setCurrentPage(1);

          const messagesRes = await axios.get(
            `${SERVER_BASE}/api/chat/sessions/${sessionIdToLoad}/messages`,
            {
              params: {userId: USER.id, page: 1, pageSize: 20}
            }
          );

          const responseData = messagesRes.data;
          const messagesList = responseData.messages || [];
          const hasMore = responseData.hasMore ?? false;
          const totalMessages = responseData.totalMessages || 0;

          //console.log('Initial load:', { hasMore, totalMessages, messagesCount: messagesList.length });

          // Set messages directly (already in correct order from server)
          setMessages(messagesList);
          setHasMoreMessages(hasMore);
          setActiveSession({
            sessionId: sessionIdToLoad,
            userId: USER.id,
            title: 'Current Session',
            roles: USER.roles,
            permissions: USER.permissions,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastMessageAt: new Date().toISOString(),
            messageCount: totalMessages,
            status: 'active'
          });
          setIsFirstMessage(false);

          const s = io(SERVER_BASE, {
            path: '/ws/socket.io',
            transports: ['websocket', 'polling'],
            withCredentials: true,
            autoConnect: true,
            forceNew: true,
            timeout: 20000,
            reconnection: true,
            reconnectionAttempts: 3
          });

          setupSocketListeners(s);

          s.on('connect', () => {
            s.emit('start_chat', {
              sessionId: sessionIdToLoad,
              userId: USER.id,
              roles: USER.roles,
              permissions: USER.permissions
            });
            setStatus('Connected to session');
            setHasError(false);
            setIsConnected(true);
          });

          setSocket(s);
          setLoadingMessages(false);
        } catch (error) {
          console.error('Failed to initialize default session:', error);
          setMessages([]);
          setLoadingMessages(false);
          setHasError(true);
          setStatus(`Failed to load session`);
        }
      };

      loadDefaultSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SERVER_BASE]);
  // Effect 3: Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({behavior: 'smooth'});
    }
  }, [messages]);

  // Effect 4: Cleanup socket on unmount
  useEffect(() => {
    return () => {
      socket?.disconnect();
    };
  }, [socket]);

  // Effect 5: Bind socket to bridge
  useEffect(() => {
    if (!socket) return;
    socket.off('action_request');
    bridge.bindSocket(socket);
  }, [socket, bridge]);

  // Effect 6: Fetch sessions
  useEffect(() => {
    if (SERVER_BASE) {
      fetchSessions();
    }
  }, [fetchSessions, SERVER_BASE]);

  // Effect 7: Initialize default tree
  useEffect(() => {
    const defaultTree: Record<string, {name: string; subs: Set<string>}> = {};
    for (const dept of FALLBACK_CATALOG.departments) {
      defaultTree[dept.departmentId] = {
        name: dept.department,
        subs: new Set(dept.subdepartments.map(s => s.subDepartmentId))
      };
    }
    setSelectedTree(defaultTree);
  }, []);

  const container = containerRef.current;
  if (!container) return null;

  const headerChipColor =
    isConnected && sessionId ? 'success' : !socket ? 'default' : 'warning';

  const ImageModal = () => {
    if (!enlargedImage) return null;

    return createPortal(
      <Box
        onClick={() => setEnlargedImage(null)}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer'
        }}>
        <Box
          component="img"
          src={enlargedImage}
          onClick={e => e.stopPropagation()}
          sx={{
            maxWidth: '90vw',
            maxHeight: '90vh',
            objectFit: 'contain',
            cursor: 'default',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
          }}
        />
        <IconButton
          onClick={() => setEnlargedImage(null)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.7)'
            }
          }}>
          <CloseIcon />
        </IconButton>
      </Box>,
      document.body
    );
  };

  return createPortal(
    <>
      <ImageModal />
      <Drawer
        anchor="left"
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        container={container}
        PaperProps={{sx: {width: 280, position: 'absolute'}}}>
        <Typography variant="h6" sx={{p: 2}}>
          Sessions
        </Typography>
        <List>
          {sessions.map(s => (
            <ListItem
              key={s.sessionId}
              selected={activeSession?.sessionId === s.sessionId}
              onMouseEnter={() => setHoveredSessionId(s.sessionId)}
              onMouseLeave={() => setHoveredSessionId(null)}
              secondaryAction={
                hoveredSessionId === s.sessionId ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={e => handleEditSession(s, e)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={e => handleDeleteSession(s, e)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null
              }
              sx={{
                pr: hoveredSessionId === s.sessionId ? 10 : 2,
                transition: 'padding-right 0.2s'
              }}>
              <ListItemButton onClick={() => handleSessionSelect(s)}>
                <ListItemText
                  primary={s.title}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: {maxWidth: '200px'}
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box
        sx={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 2147483647
        }}>
        <Tooltip title={open ? 'Close chat' : 'Open chat'}>
          <Fab color="primary" onClick={() => setOpen(o => !o)}>
            <Badge
              variant="dot"
              color={isConnected ? 'success' : 'error'}
              overlap="circular"
              anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
              sx={{'& .MuiBadge-badge': {transform: 'scale(0.7)'}}}>
              <ChatIcon fontSize="small" />
            </Badge>
          </Fab>
        </Tooltip>
      </Box>

      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 84,
            width: expanded ? 720 : 520,
            maxWidth: '95vw',
            height: expanded ? '80vh' : '75vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2
          }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{p: 1.25, borderBottom: '1px solid #eee'}}>
            <IconButton onClick={() => setShowDrawer(true)} aria-label="Menu">
              <MenuIcon />
            </IconButton>
            <Typography
              variant="subtitle1"
              sx={{fontWeight: 700, flex: 1}}
              noWrap>
              {activeSession
                ? truncateText(activeSession.title, 25)
                : 'AI ASSISTANT'}
            </Typography>
            <Chip
              size="small"
              label={connectionStatus()}
              color={headerChipColor as any}
              variant="outlined"
              sx={{mr: 1}}
            />
            <Tooltip title="Start New Session">
              <IconButton
                color="primary"
                onClick={startNewSession}
                size="small">
                <AddIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={() => setExpanded(x => !x)}
              aria-label="Expand">
              {expanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={{px: 1.25, pt: 1, pb: 0.5}}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <FormControl size="small" sx={{minWidth: 140}}>
                <InputLabel id="resp-style">Response Style</InputLabel>
                <Select
                  labelId="resp-style"
                  value={responseMode}
                  label="Response Style"
                  onChange={e => {
                    const v = String(e.target.value || 'summary');
                    setResponseMode(v);
                    try {
                      localStorage.setItem('responseMode', v);
                    } catch {}
                  }}>
                  <MenuItem value="short">Short</MenuItem>
                  <MenuItem value="summary">Summary</MenuItem>
                  <MenuItem value="detailed">Detailed</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{minWidth: 180}}>
                <InputLabel id="source-style">Source</InputLabel>
                <Select
                  labelId="source-style"
                  value={source}
                  label="Source"
                  onChange={e => {
                    const v = String(e.target.value);
                    setSource(v);
                    try {
                      localStorage.setItem('source', v);
                    } catch {}
                  }}>
                  <MenuItem value="documents">Documents</MenuItem>
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="documents_and_web">
                    Documents and Web
                  </MenuItem>
                  <MenuItem value="gpt-5">LLM</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Box>

          {hasError && status ? (
            <Box sx={{px: 1.25, pt: 1}}>
              <Alert severity="error">{status}</Alert>
            </Box>
          ) : null}

          <Box
            ref={messagesContainerRef}
            onScroll={handleScroll}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1.25,
              py: 1,
              bgcolor: '#fafafa'
            }}>
            {loadingOlderMessages && (
              <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                <CircularProgress size={20} />
              </Box>
            )}

            {loadingMessages ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%'
                }}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'text.secondary',
                  textAlign: 'center',
                  gap: 2
                }}>
                <SmartToyIcon sx={{fontSize: 64, color: 'primary.main'}} />
                <Typography variant="subtitle1">
                  Start chatting with AI Assistant
                </Typography>
                <Typography variant="body2">
                  Click <b>➕</b> for a new session or select from history.
                </Typography>
                <Stack direction="row" spacing={3} sx={{mt: 2}}>
                  <Stack alignItems="center" spacing={0.5}>
                    <DescriptionIcon />
                    <Typography variant="caption">Documents</Typography>
                  </Stack>
                  <Stack alignItems="center" spacing={0.5}>
                    <LanguageIcon />
                    <Typography variant="caption">Web</Typography>
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <>
                {messages.map((m, i) => {
                  const bg =
                    m.role === 'user'
                      ? '#e3f2fd'
                      : m.role === 'assistant'
                        ? '#e8f5e9'
                        : '#ffebee';
                  const color =
                    m.role === 'user'
                      ? 'primary.main'
                      : m.role === 'assistant'
                        ? 'success.main'
                        : 'error.main';

                  const hasDocumentReferences =
                    m.role === 'assistant' &&
                    Array.isArray(m.sources) &&
                    m.sources.includes('document') &&
                    ((Array.isArray(m.thumbnails) && m.thumbnails.length > 0) ||
                      (Array.isArray(m.vector) && m.vector.length > 0));

                  const docItems = hasDocumentReferences
                    ? m.thumbnails && m.thumbnails.length
                      ? m.thumbnails
                      : (m.vector || []).slice(0, 12)
                    : [];

                  const hasWebReferences =
                    m.role === 'assistant' &&
                    Array.isArray(m.webSources) &&
                    m.webSources.length > 0 &&
                    Array.isArray(m.sources) &&
                    m.sources.includes('web');

                  return (
                    <Paper
                      key={i}
                      variant="outlined"
                      sx={{bgcolor: bg, borderRadius: 1.5, p: 1, mb: 1}}>
                      <Typography
                        variant="caption"
                        sx={{
                          color,
                          fontWeight: 700,
                          textTransform: 'capitalize'
                        }}>
                        {m.role}
                      </Typography>
                      <Box
                        sx={{mt: 0.5, wordBreak: 'break-word'}}
                        dangerouslySetInnerHTML={{
                          // ---- SECURITY: scrub script/event-handler payloads
                          // out of the markdown-rendered string before
                          // injecting as HTML. ----
                          __html: sanitizeHtml(renderMarkdown(m.text))
                        }}
                      />

                      {hasDocumentReferences && (
                        <>
                          <Divider sx={{my: 1}} />
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{color: 'text.secondary', fontWeight: 600}}>
                              Document References
                            </Typography>
                            <Grid container spacing={1} sx={{mt: 0.5}}>
                              {docItems.map((doc: any, idx: number) => {
                                const rawThumb =
                                  doc.thumbUrl ||
                                  doc.pageApiUrl ||
                                  (doc.page
                                    ? `/api/docs/${doc.docId}/pages/${doc.page}`
                                    : doc.pageImageUrl || doc.blobUrl || '#');
                                const thumbUrl = toServerUrl(rawThumb);
                                const linkUrl = toServerUrl(
                                  doc.linkUrl ||
                                    doc.pdfBlobUrl ||
                                    doc.blobUrl ||
                                    '#'
                                );
                                const docName =
                                  doc.docName ||
                                  doc.documentName ||
                                  doc.filename ||
                                  `Document ${String(doc.docId || '').slice(0, 8)}`;

                                return (
                                  <Grid item xs={4} sm={3} key={idx}>
                                    <Tooltip title={docName} arrow>
                                      <Card
                                        variant="outlined"
                                        component="a"
                                        href={linkUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        sx={{textDecoration: 'none'}}>
                                        <CardMedia
                                          component="img"
                                          src={thumbUrl}
                                          alt={`${docName} page ${doc.page ?? '-'}`}
                                          onClick={e => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setEnlargedImage(thumbUrl);
                                          }}
                                          sx={{
                                            height: 100,
                                            objectFit: 'contain',
                                            bgcolor: '#f0f0f0',
                                            cursor: 'zoom-in',
                                            transition: 'transform 0.2s',
                                            '&:hover': {
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                        />
                                        <CardContent sx={{p: 0.5}}>
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            noWrap>
                                            Page {doc.page ?? '-'}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            display="block"
                                            color="text.secondary"
                                            noWrap>
                                            {truncateText(docName, 15)}
                                          </Typography>
                                        </CardContent>
                                      </Card>
                                    </Tooltip>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        </>
                      )}

                      {hasWebReferences && (
                        <>
                          <Divider sx={{my: 1}} />
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                mb: 0.5,
                                display: 'block'
                              }}>
                              Web References
                            </Typography>
                            <Stack
                              component="ul"
                              sx={{pl: 2, mb: 0, mt: 0.5}}
                              spacing={0.5}>
                              {m.webSources!.map((s, idx) => (
                                <Stack
                                  key={idx}
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.75}
                                  component="li"
                                  sx={{fontSize: '0.75rem'}}>
                                  <LinkIcon
                                    sx={{fontSize: 14, color: 'text.secondary'}}
                                  />
                                  <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      textDecoration: 'none',
                                      color: 'inherit',
                                      wordBreak: 'break-word'
                                    }}>
                                    <Typography variant="caption">
                                      {s.title || s.url}
                                    </Typography>
                                  </a>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        </>
                      )}
                    </Paper>
                  );
                })}

                {isAssistantThinking && (
                  <Paper
                    variant="outlined"
                    sx={{bgcolor: '#e8f5e9', borderRadius: 1.5, p: 1, mb: 1}}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'success.main',
                        fontWeight: 700,
                        textTransform: 'capitalize'
                      }}>
                      Assistant
                    </Typography>
                    <ThinkingIndicator />
                  </Paper>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box
            component="form"
            sx={{p: 1.25, borderTop: '1px solid #eee'}}
            onSubmit={e => {
              e.preventDefault();
              e.stopPropagation();
              sendMessage();
            }}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                placeholder={
                  sessionId && isConnected
                    ? 'Ask something...'
                    : 'Connect to a session first...'
                }
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation(); // keep Enter from affecting other components
                    sendMessage();
                  }
                }}
                disabled={!sessionId || !isConnected}
              />
              <Button
                type="submit" // Enter will submit only this form
                variant="contained"
                endIcon={<SendIcon />}
                onClick={sendMessage}
                disabled={!sessionId || !isConnected}>
                Send
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}
    </>,
    container
  );
};

export default GlobalChatWidget;

